import type { NavLayoutSpec } from "@exxatdesignux/product-framework"
import { collectNavUrls } from "@exxatdesignux/ui/lib/nav-active"

import { isProgramManagementPath } from "@/lib/demo-shell-routes"

/** Program Details tabs shown in the Exxat primary sidebar. */
export const PROGRAM_MANAGEMENT_TABS = [
  {
    id: "setup",
    label: "Setup",
    segment: "setup",
    icon: "fa-light fa-sliders",
  },
  {
    id: "standards",
    label: "Standards",
    segment: "standards",
    icon: "fa-light fa-list-check",
  },
  {
    id: "notification-hub",
    label: "Notification Hub",
    segment: "notification-hub",
    icon: "fa-light fa-bell",
  },
  {
    id: "user-roles",
    label: "User Roles",
    segment: "user-roles",
    icon: "fa-light fa-users",
  },
  {
    id: "data-feed",
    label: "Data Feed",
    segment: "data-feed",
    icon: "fa-light fa-database",
  },
  {
    id: "program-360",
    label: "Program 360",
    segment: "program-360",
    icon: "fa-light fa-chart-mixed",
  },
  {
    id: "calendar",
    label: "Calendar",
    segment: "calendar",
    icon: "fa-light fa-calendar",
  },
  {
    id: "integrations",
    label: "Integrations",
    segment: "integrations",
    icon: "fa-light fa-plug",
  },
] as const

export type ProgramManagementTabId = (typeof PROGRAM_MANAGEMENT_TABS)[number]["id"]

/** Base path for program management primary nav (`/programs` or `/prism/program-details`). */
export function programManagementNavBase(pathname: string): string {
  if (pathname === "/programs" || pathname.startsWith("/programs/")) return "/programs"
  const match = pathname.match(/^(\/[^/]+)\/program-details(?:\/|$)/)
  if (match) return `${match[1]}/program-details`
  return "/programs"
}

export function programManagementTabHref(base: string, segment: string): string {
  if (segment === "setup") {
    return base.endsWith("/program-details") ? `${base}/setup` : base
  }
  return `${base}/${segment}`
}

export function buildProgramManagementSidebarNavLayout(basePath: string): NavLayoutSpec {
  return {
    preamble: PROGRAM_MANAGEMENT_TABS.map(tab => ({
      key: tab.id,
      title: tab.label,
      url: programManagementTabHref(basePath, tab.segment),
      iconClass: tab.icon,
      iconActiveClass: tab.icon.replace("fa-light", "fa-solid"),
    })),
    sections: [],
  }
}

export function programManagementNavUrls(pathname: string): string[] {
  const base = programManagementNavBase(pathname)
  return collectNavUrls(buildProgramManagementSidebarNavLayout(base).preamble)
}

export function activeProgramManagementTabId(
  pathname: string,
): ProgramManagementTabId | null {
  const base = programManagementNavBase(pathname)
  if (
    pathname === base ||
    pathname === `${base}/` ||
    pathname === `${base}/setup`
  ) {
    return "setup"
  }

  for (const tab of PROGRAM_MANAGEMENT_TABS) {
    if (tab.id === "setup") continue
    if (pathname === `${base}/${tab.segment}` || pathname.startsWith(`${base}/${tab.segment}/`)) {
      return tab.id
    }
  }

  return null
}

export function isProgramManagementPrimaryNavActive(
  navKey: string,
  pathname: string,
): boolean | undefined {
  const active = activeProgramManagementTabId(pathname)
  if (!active) return undefined
  return navKey === active
}
